// CoreUI Pro source reference: /Users/guochunzhong/git/oss/coreui-pro/js/src/calendar.js
import BaseComponent from "./base_component"
import {
  dateDisabled,
  dateRangeContainsDisabled,
  initialCalendarDate,
  localDate,
  parseDate,
  periodDisabled,
  sameDay,
} from "./dom"
import { setTemplate } from "./template"

const DAY = 86_400_000

function startOfDay(date) {
  return localDate(date.getFullYear(), date.getMonth(), date.getDate())
}

// Calendar days can be shorter or longer than 24 hours at a daylight saving transition.
function addDays(date, days) {
  return localDate(date.getFullYear(), date.getMonth(), date.getDate() + days)
}

function withTime(date, previous) {
  if (!date || !previous) return date
  const result = new Date(date)
  result.setHours(previous.getHours(), previous.getMinutes(), previous.getSeconds(), previous.getMilliseconds())
  return result
}

function startOfWeek(date, firstDay) {
  const result = startOfDay(date)
  result.setDate(result.getDate() - ((result.getDay() - firstDay + 7) % 7))
  return result
}

function weekNumber(date) {
  const target = startOfDay(date)
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7))
  const firstThursday = localDate(target.getFullYear(), 0, 4)
  return 1 + Math.round((target - firstThursday) / (7 * DAY) - (3 - ((firstThursday.getDay() + 6) % 7)) / 7)
}

function inRange(date, start, end) {
  if (!start || !end) return false
  const timestamp = startOfDay(date).getTime()
  return (
    timestamp >= Math.min(startOfDay(start), startOfDay(end)) &&
    timestamp <= Math.max(startOfDay(start), startOfDay(end))
  )
}

export default class Calendar extends BaseComponent {
  static componentName = "calendar"
  static defaults = {
    calendars: 1,
    firstDayOfWeek: 1,
    locale: "default",
    range: false,
    selectEndDate: false,
    selectionType: "day",
    showAdjacementDays: true,
    showWeekNumber: false,
  }

  constructor(element, config = {}) {
    super(element, config)
    this.calendarDate = initialCalendarDate(this.config)
    this.startDate = parseDate(this.config.startDate, this.config.selectionType)
    this.endDate = parseDate(this.config.endDate, this.config.selectionType)
    this.selectEndDate = this.config.selectEndDate
    this.view =
      { day: "days", week: "days", month: "months", quarter: "quarters", year: "years" }[this.config.selectionType] ||
      "days"
    this.render()
    this.listen(element, "click", (event) => this.handleClick(event))
    this.listen(element, "keydown", (event) => {
      if (
        (event.key === "Enter" || event.key === " ") &&
        event.target.matches(".calendar-cell[tabindex='0'], .calendar-row[tabindex='0']")
      ) {
        event.preventDefault()
        this.select(parseDate(event.target.dataset.coreuiDate))
      }
    })
  }

  update(config = {}) {
    this.config = { ...this.config, ...config }
    this.startDate = parseDate(this.config.startDate, this.config.selectionType)
    this.endDate = parseDate(this.config.endDate, this.config.selectionType)
    if (Object.prototype.hasOwnProperty.call(config, "selectEndDate")) this.selectEndDate = config.selectEndDate
    if (this.config.calendarDate)
      this.calendarDate = parseDate(this.config.calendarDate, this.config.selectionType) || this.calendarDate
    this.render()
  }

  handleClick(event) {
    const button = event.target.closest("button")
    if (button?.classList.contains("btn-prev")) return this.moveMonth(-1)
    if (button?.classList.contains("btn-next")) return this.moveMonth(1)
    if (button?.classList.contains("btn-double-prev")) return this.moveYear(-1)
    if (button?.classList.contains("btn-double-next")) return this.moveYear(1)
    if (button?.classList.contains("btn-month")) {
      this.view = "months"
      return this.render()
    }
    if (button?.classList.contains("btn-year")) {
      this.view = "years"
      return this.render()
    }

    const cell = event.target.closest(".calendar-cell[tabindex='0'], .calendar-row[tabindex='0']")
    if (cell) this.select(parseDate(cell.dataset.coreuiDate))
  }

  moveMonth(amount) {
    this.calendarDate = localDate(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + amount, 1)
    this.render()
    this.emit("calendarDateChange", { date: this.calendarDate })
  }

  moveYear(amount) {
    this.calendarDate = localDate(this.calendarDate.getFullYear() + amount, this.calendarDate.getMonth(), 1)
    this.render()
    this.emit("calendarDateChange", { date: this.calendarDate })
  }

  select(date) {
    const period = { months: "month", quarters: "quarter", years: "year" }[this.view]
    if (!date || periodDisabled(date, period, this.config)) return

    if (this.view === "years" && this.config.selectionType !== "year") {
      this.calendarDate = localDate(date.getFullYear(), this.calendarDate.getMonth(), 1)
      this.view = { month: "months", quarter: "quarters" }[this.config.selectionType] || "days"
      return this.render()
    }
    if (this.view === "months" && this.config.selectionType !== "month") {
      this.calendarDate = localDate(date.getFullYear(), date.getMonth(), 1)
      this.view = "days"
      return this.render()
    }

    const changed = []
    const setDate = (part, value) => {
      this[`${part}Date`] = withTime(value, this[`${part}Date`])
      changed.push(part)
    }
    const selectEnd = (value) => {
      this.selectEndDate = value
      this.emit("selectEndChange", { value })
    }
    if (this.config.selectionType === "week") {
      date = startOfWeek(date, Number(this.config.firstDayOfWeek))
      this.startDate = date
      this.endDate = addDays(date, 6)
      changed.push("start", "end")
    } else if (this.config.range) {
      if (this.selectEndDate) {
        if (this.startDate && this.startDate > date) {
          setDate("start", date)
          setDate("end", null)
        } else {
          selectEnd(false)
          if (dateRangeContainsDisabled(this.startDate, date, this.config.disabledDates)) {
            setDate("start", null)
            setDate("end", null)
          } else setDate("end", date)
        }
      } else if (this.endDate && this.endDate < date) {
        setDate("start", date)
        setDate("end", null)
        selectEnd(true)
      } else if (dateRangeContainsDisabled(date, this.endDate, this.config.disabledDates)) {
        setDate("start", null)
        setDate("end", null)
      } else {
        selectEnd(true)
        setDate("start", date)
      }
    } else {
      setDate("start", date)
    }

    this.config.startDate = this.startDate
    this.config.endDate = this.endDate
    this.render()
    for (const part of changed) this.emit(`${part}DateChange`, { date: this[`${part}Date`] })
    this.config.onSelect?.(this.startDate, this.endDate, changed)
  }

  render() {
    this.element.replaceChildren()
    this.element.className = `${this.originalClassName || this.element.className} calendars`.trim()
    this.element.classList.toggle("show-week-numbers", Boolean(this.config.showWeekNumber))
    this.element.classList.add(`select-${this.config.selectionType}`)
    for (let index = 0; index < Number(this.config.calendars || 1); index += 1) {
      this.element.append(this.renderPanel(index))
    }
  }

  renderPanel(index) {
    const date =
      this.view === "years"
        ? localDate(this.calendarDate.getFullYear() + index * 12, 0, 1)
        : localDate(this.calendarDate.getFullYear(), this.calendarDate.getMonth() + index, 1)
    const panel = document.createElement("div")
    panel.className = "calendar"
    panel.dataset.coreuiCalendarIndex = index
    panel.append(this.renderNavigation(date), this.renderTable(date))
    return panel
  }

  renderNavigation(date) {
    const navigation = document.createElement("div")
    navigation.className = "calendar-nav"
    const month = date.toLocaleDateString(this.config.locale, { month: "long" })
    const year = date.toLocaleDateString(this.config.locale, { year: "numeric" })
    navigation.innerHTML = `
      <div class="calendar-nav-prev">
        <button type="button" class="calendar-nav-btn btn-double-prev" aria-label="Previous year"><span class="calendar-nav-icon calendar-nav-icon-double-prev"></span></button>
        ${this.view === "days" ? '<button type="button" class="calendar-nav-btn btn-prev" aria-label="Previous month"><span class="calendar-nav-icon calendar-nav-icon-prev"></span></button>' : ""}
      </div>
      <div class="calendar-nav-date" aria-live="polite">
        ${this.view === "days" ? `<button type="button" class="calendar-nav-btn btn-sm btn-month">${month}</button>` : ""}
        <button type="button" class="calendar-nav-btn btn-year">${year}</button>
      </div>
      <div class="calendar-nav-next">
        ${this.view === "days" ? '<button type="button" class="calendar-nav-btn btn-next" aria-label="Next month"><span class="calendar-nav-icon calendar-nav-icon-next"></span></button>' : ""}
        <button type="button" class="calendar-nav-btn btn-double-next" aria-label="Next year"><span class="calendar-nav-icon calendar-nav-icon-double-next"></span></button>
      </div>`
    return navigation
  }

  renderTable(date) {
    const table = document.createElement("table")
    if (this.view === "days") this.renderDays(table, date)
    if (this.view === "months") this.renderMonths(table, date)
    if (this.view === "quarters") this.renderQuarters(table, date)
    if (this.view === "years") this.renderYears(table, date)
    return table
  }

  renderDays(table, monthDate) {
    const headerStart = startOfWeek(monthDate, Number(this.config.firstDayOfWeek))
    const weekdays = Array.from({ length: 7 }, (_, index) => addDays(headerStart, index))
    const thead = document.createElement("thead")
    const header = document.createElement("tr")
    if (this.config.showWeekNumber)
      header.insertAdjacentHTML(
        "beforeend",
        '<th class="calendar-cell"><div class="calendar-header-cell-inner"></div></th>',
      )
    weekdays.forEach((date) => {
      const cell = document.createElement("th")
      cell.className = "calendar-cell"
      cell.abbr = date.toLocaleDateString(this.config.locale, { weekday: "long" })
      cell.innerHTML = `<div class="calendar-header-cell-inner">${cell.abbr.slice(0, 2)}</div>`
      header.append(cell)
    })
    thead.append(header)
    table.append(thead)

    const tbody = document.createElement("tbody")
    const first = startOfWeek(monthDate, Number(this.config.firstDayOfWeek))
    for (let week = 0; week < 6; week += 1) {
      const row = document.createElement("tr")
      row.className = "calendar-row"
      if (this.config.selectionType === "week") row.tabIndex = 0
      const weekStart = addDays(first, week * 7)
      row.dataset.coreuiDate = weekStart.toString()
      if (this.config.showWeekNumber)
        row.insertAdjacentHTML("beforeend", `<th class="calendar-cell-week-number">${weekNumber(weekStart)}</th>`)
      for (let day = 0; day < 7; day += 1) row.append(this.renderDayCell(addDays(weekStart, day), monthDate.getMonth()))
      tbody.append(row)
    }
    table.append(tbody)
  }

  renderDayCell(date, currentMonth) {
    const cell = document.createElement("td")
    const adjacent = date.getMonth() !== currentMonth
    const disabled = dateDisabled(date, this.config) || (adjacent && !this.config.selectAdjacementDays)
    cell.className = [
      "calendar-cell",
      adjacent ? (date < localDate(date.getFullYear(), currentMonth, 1) ? "previous" : "next") : "current",
      sameDay(date, new Date()) ? "today" : "",
      sameDay(date, this.startDate) || sameDay(date, this.endDate) ? "selected" : "",
      inRange(date, this.startDate, this.endDate) ? "range" : "",
      disabled ? "disabled" : "",
    ]
      .filter(Boolean)
      .join(" ")
    cell.tabIndex = disabled ? -1 : 0
    cell.dataset.coreuiDate = date.toString()
    cell.setAttribute("aria-label", date.toLocaleDateString(this.config.locale))
    if (sameDay(date, this.startDate) || sameDay(date, this.endDate)) cell.setAttribute("aria-selected", "true")
    cell.innerHTML = `<div class="calendar-cell-inner day">${adjacent && !this.config.showAdjacementDays ? "" : date.toLocaleDateString(this.config.locale, { day: "numeric" })}</div>`
    if (this.config.renderDayCell && (!adjacent || this.config.showAdjacementDays))
      setTemplate(
        cell.firstElementChild,
        this.config.renderDayCell(date, {
          isDisabled: disabled,
          isInCurrentMonth: !adjacent,
          isSelected: sameDay(date, this.startDate) || sameDay(date, this.endDate),
        }),
      )
    return cell
  }

  renderMonths(table, date) {
    const body = document.createElement("tbody")
    for (let rowIndex = 0; rowIndex < 4; rowIndex += 1) {
      const row = document.createElement("tr")
      for (let column = 0; column < 3; column += 1) {
        const month = rowIndex * 3 + column
        const value = localDate(date.getFullYear(), month, 1)
        row.append(
          this.renderSimpleCell(value, value.toLocaleDateString(this.config.locale, { month: "short" }), "month"),
        )
      }
      body.append(row)
    }
    table.append(body)
  }

  renderQuarters(table, date) {
    const body = document.createElement("tbody")
    const row = document.createElement("tr")
    for (let quarter = 0; quarter < 4; quarter += 1)
      row.append(this.renderSimpleCell(localDate(date.getFullYear(), quarter * 3, 1), `Q${quarter + 1}`, "quarter"))
    body.append(row)
    table.append(body)
  }

  renderYears(table, date) {
    const body = document.createElement("tbody")
    const firstYear = Math.floor(date.getFullYear() / 12) * 12
    for (let rowIndex = 0; rowIndex < 4; rowIndex += 1) {
      const row = document.createElement("tr")
      for (let column = 0; column < 3; column += 1) {
        const value = localDate(firstYear + rowIndex * 3 + column, 0, 1)
        row.append(this.renderSimpleCell(value, value.getFullYear(), "year"))
      }
      body.append(row)
    }
    table.append(body)
  }

  renderSimpleCell(date, label, className) {
    const cell = document.createElement("td")
    const disabled = periodDisabled(date, className, this.config)
    const matches = (value) =>
      value?.getFullYear() === date.getFullYear() &&
      (className === "year" ||
        (className === "quarter"
          ? Math.floor(value.getMonth() / 3) === Math.floor(date.getMonth() / 3)
          : value.getMonth() === date.getMonth()))
    const selected = this.config.selectionType === className && (matches(this.startDate) || matches(this.endDate))
    cell.className = [
      "calendar-cell",
      disabled ? "disabled" : "",
      selected ? "selected" : "",
      inRange(date, this.startDate, this.endDate) ? "range" : "",
    ]
      .filter(Boolean)
      .join(" ")
    cell.tabIndex = disabled ? -1 : 0
    cell.dataset.coreuiDate = date.toString()
    cell.setAttribute("aria-label", `${label}, ${date.getFullYear()}`)
    cell.setAttribute("aria-selected", selected)
    cell.innerHTML = `<div class="calendar-cell-inner ${className}">${label}</div>`
    const renderCell = this.config[`render${className[0].toUpperCase()}${className.slice(1)}Cell`]
    if (renderCell)
      setTemplate(cell.firstElementChild, renderCell(date, { isDisabled: disabled, isSelected: selected }))
    return cell
  }

  dispose() {
    if (this.signal.aborted) return
    this.element.innerHTML = this.originalHTML
    this.element.className = this.originalClassName
    super.dispose()
  }
  snapshotState() {
    return {
      config: {
        calendarDate: this.calendarDate,
        startDate: this.startDate,
        endDate: this.endDate,
        selectEndDate: this.selectEndDate,
      },
      view: this.view,
    }
  }
  restoreState(state) {
    if (state.view) {
      this.view = state.view
      this.render()
    }
  }
}
